import React, { useState, useMemo, useEffect } from "react";

import {
  RideSelectTextField,
  RideSelect,
  RideTextField,
} from "@rimac-seguros/ride-system-components";

import { LIST_TYPE_ROAD } from "@/constants/list-type-roads";
import { CustomListBottomSheet } from "@/components/molecules/CustomListBottomSheet";
import { useUbigeoBeneficiary } from "@/hooks/useUbigeoBeneficiary";
import { CustomDropdown } from "@/components/molecules/CustomDropdown";

import "./styles.scss";
import helpers from '@/utils/helpers'

export const FormUbigeo = ({
  register,
  prefixControl,
  prefixError,
  setValue,
  watch,
  isSmallScreen,
  loadingSave,
}) => {
  const isVE = helpers.validateVidaEndosable();
  const [openStreetType, setOpenStreetType] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    departments,
    provinces,
    districts,
    loadingDepartments,
    loadingProvinces,
    loadingDistricts,
    getProvinces,
    getDistricts,
    getProvinceDistrictTogether,
  } = useUbigeoBeneficiary({
    preloadProvinces: watch(`${prefixControl}.provinceList`) || [],
    preloadDistricts: watch(`${prefixControl}.districtList`) || [],
  });

  const currentDepartment = watch(`${prefixControl}.department`);
  const currentProvince = watch(`${prefixControl}.province`);

  useEffect(() => {
    if (currentDepartment && currentProvince) {
      getProvinceDistrict();
    }
  }, []);

  const getProvinceDistrict = async () => {
    setLoading(true);
    await getProvinceDistrictTogether(currentDepartment, currentProvince);
    setLoading(false);
  };

  const optionDepartments = useMemo(
    () =>
      departments.map((item) => ({
        label: item.descripcionDepartamento,
        value: item.ideDepartamento,
      })),
    [departments]
  );

  const optionProvinces = useMemo(
    () =>
      provinces.map((item) => ({
        label: item.descripcionProvincia,
        value: item.ideProvincia,
      })),
    [provinces]
  );

  const optionDistricts = useMemo(
    () =>
      districts.map((item) => ({
        label: item.descripcionDistrito,
        value: item.ideDistrito,
      })),
    [districts]
  );

  const handleInputStreetNumber = (e) => {
    const el = e.target;
    el.value = el.value.replace(/[^0-9]/g, "");
  };

  return (
    <div className="grid modal-ab_ubigeo gap-y-ride-gl-spacing-2xlarge lg:gap-y-ride-gl-spacing-3xlarge lg:gap-x-ride-gl-spacing-2xlarge lg:grid-cols-2">
      <CustomDropdown
        name={`${prefixControl}.department`}
        label="Departamento"
        titleModal="Departamento"
        isShowModal={isSmallScreen}
        hasSearch
        iconResult="RideGlPinSolid"
        list={optionDepartments || []}
        loading={loadingDepartments}
        disabled={loadingDepartments || loading || loadingSave}
        register={register}
        setValue={setValue}
        watch={watch}
        onSelect={(value) => {
          getProvinces(value);
          setValue(`${prefixControl}.departmentList`, departments);
          setValue(`${prefixControl}.province`, "");
          setValue(`${prefixControl}.district`, "");
        }}
        error={!!prefixError?.department}
        errorMessage={isVE ? "Elige un departamento" : "Este campo es obligatorio"}
        customClass="col-span-full"
      />
      <CustomDropdown
        name={`${prefixControl}.province`}
        label="Provincia"
        titleModal="Provincia"
        isShowModal={isSmallScreen}
        hasSearch
        iconResult="RideGlPinSolid"
        list={optionProvinces || []}
        loading={loadingProvinces}
        disabled={
          !watch(`${prefixControl}.department`) ||
          loadingProvinces ||
          loadingSave
        }
        register={register}
        setValue={setValue}
        watch={watch}
        onSelect={(value) => {
          getDistricts(watch(`${prefixControl}.department`), value);
          setValue(`${prefixControl}.provinceList`, provinces);
          setValue(`${prefixControl}.district`, "");
        }}
        error={!!prefixError?.province}
        errorMessage={isVE ? "Elige una provincia" : "Este campo es obligatorio"}
      />
      <CustomDropdown
        name={`${prefixControl}.district`}
        label="Distrito"
        titleModal="Distrito"
        isShowModal={isSmallScreen}
        hasSearch
        iconResult="RideGlPinSolid"
        list={optionDistricts || []}
        loading={loadingDistricts}
        disabled={
          !watch(`${prefixControl}.province`) || loadingDistricts || loadingSave
        }
        register={register}
        setValue={setValue}
        watch={watch}
        onSelect={() => setValue(`${prefixControl}.districtList`, districts)}
        error={!!prefixError?.district}
        errorMessage={isVE ? "Elige un distrito" : "Este campo es obligatorio"}
      />
      <div className="relative flex flex-col col-span-full gap-y-ride-gl-spacing-2xlarge lg:gap-y-ride-gl-spacing-3xlarge lg:gap-x-ride-gl-spacing-2xlarge lg:flex-row">
        <RideSelectTextField
          label="Dirección"
          size="large"
          disabled={loadingSave}
          error={!!prefixError?.address}
          errorMessage={prefixError?.address?.message}
          className="w-full field-capitalize"
        >
          <RideSelect
            {...register(`${prefixControl}.streetType`, { required: true })}
            options={LIST_TYPE_ROAD}
            value={watch(`${prefixControl}.streetType`)}
            onOpenChange={(open) => isSmallScreen && setOpenStreetType(open)}
          />
          <RideTextField
            {...register(`${prefixControl}.address`, {
              required: isVE ? "Ingresa una dirección" : "Este campo es obligatorio",
              minLength: {
                value: 3,
                message: "Debes ingresar mínimo 3 caracteres",
              },
              validate: (value) =>
                value.trim().length >= 3 ||
                "Debes ingresar mínimo 3 caracteres válidos",
            })}
            onChange={(e) =>
              setValue(`${prefixControl}.address`, e.target.value)
            }
          />
        </RideSelectTextField>
        <div
          className="form-ubigeo_mask"
          onClick={() => setOpenStreetType(true)}
        />
        <CustomListBottomSheet
          title="Tipo de vía"
          list={LIST_TYPE_ROAD}
          isOpen={openStreetType}
          setIsOpen={setOpenStreetType}
          name={`${prefixControl}.streetType`}
          setValue={setValue}
          value={watch(`${prefixControl}.streetType`)}
        />
        <RideTextField
          {...register(`${prefixControl}.streetNumber`, {
            required: isVE ? "Ingresa un número de vía" : false,
            maxLength: {
              value: 10,
              message: "Número de vía es incorrecto",
            },
          })}
          label="Nro. de via"
          aria-required
          size="large"
          className="modal-ab_ubigeo-streetNumber"
          disabled={loadingSave}
          error={!!prefixError?.streetNumber}
          errorMessage={prefixError?.streetNumber?.message}
          onInput={handleInputStreetNumber}
          onChange={(e) =>
            setValue(`${prefixControl}.streetNumber`, e.target.value)
          }
        />
      </div>
    </div>
  );
};
